import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import type { BoardTableState } from "../gen/bazivan/v1/table_pb";
import { client } from "../net/client";
import { Overlay } from "../ui/Overlay";
import { ResultAd } from "../ui/Ads";
import { avatarSrc } from "../game/avatar";
import { botBare } from "../game/seats";
import { BoardChatButtons } from "../game/RoundSeat";
import { voiceHold } from "../ui/VoiceButton";
import { synth } from "../game/sfx";
import { breakStreak, newStreak, outSound } from "./streak";
import { BoardView, type BoardHandle, PLAYER_COLORS } from "./BoardView";
import { type Board, blockedAt, occupancy, ray } from "./board";
import { FREEZE_MS, GRACE_MS } from "./race";
import { PICTURES, type PictureId } from "./pictures";
import { generateShared } from "./shared";
import { HANDMADE, pictureName } from "./stages";
import { BreakWatch } from "../game/handBreak";
import { BreakNext } from "../game/BreakNext";
import { CoopTable } from "./CoopTable";
import { type Tail, tailOf } from "./online";
import { Dock, FreezeRing, Intro, type Lane, Standings, Track, shake, teamBox, useInsets } from "./RaceView";
import { clock, fa } from "./fa";

/**
 * میزِ آنلاینِ فلش‌ها (اتاقِ دوستان) — همان صفحه‌ی مسابقه‌ی آفلاین، ولی داور سرور است
 * (Backend/internal/boards/arrows/game.go). رمزِ نما:
 *
 *   phase ۰ شمارش (تخته هنوز نیامده؛ extra[0] منفیِ مانده تا go)، ۱ مسابقه، ۲ پایان
 *   board [شماره‌ی نقش، شمارِ بازیکن، فلشِ هر کس، بیشترین درازا، بذر، تیمی] — تخته از همین بذر همان‌جا ساخته می‌شود
 *   hand  فلش‌های برداشته؛ score برداشته‌ی هر رنگ (هر که زده باشد)
 *   extra [میلی‌ثانیه از go، لحظه‌ی نفرِ اول (تیمی: تیمِ اول) یا −۱، و برای هر صندلی: لمسِ اشتباه، یخِ مانده،
 *         لحظه‌ی تمام یا −۱؛ بعد برای هر صندلی: فلش‌هایی که از یارش زد]
 *
 * تیمی (۲ به ۲، روبه‌روها یار): کسی که فلش‌هایش تمام شد فلش‌های یارش را می‌زند (کاربر، ۷ آبان) — همان لحظه
 * فلش‌های یار روشن و لمس‌شدنی می‌شوند و برچسبِ «کمک به …» بالای صفحه می‌آید.
 *
 * لمس بی‌درنگ روی صفحه دیده می‌شود (فلشی که این‌جا آزاد است همان‌جا هم آزاد است، چون تخته و فلش‌های رفته
 * یکی‌اند) و سرور با رویدادِ out همان را تأیید می‌کند. اگر سرور نپذیرفت (مثلاً دو لمس زودتر از کفِ فاصله رسیدند)،
 * نمای بعدی فلش را برمی‌گرداند — پس خطای شبکه نمی‌تواند تخته را برای همیشه از سرور جدا کند.
 */
const GAP_MS = 200;
/** فلشی که این‌جا رفت و سرور تأییدش نکرد، بعد از این مدت برمی‌گردد. */
const CONFIRM_MS = 1500;

/** میزِ آنلاینِ فلش‌ها: مسابقه، یا همکاری روی نقشه‌ی بزرگ (CoopTable.tsx). حالت در طولِ میز عوض نمی‌شود. */
export function ArrowsTable({ state }: { state: BoardTableState }) {
  return tailOf(state.extra, state.seats.length).mode === 1 ? <CoopTable state={state} /> : <RaceTable state={state} />;
}

function RaceTable({ state }: { state: BoardTableState }) {
  const h = state.board;
  const board = useMemo<Board | null>(() => {
    if (h.length < 5) return null;
    const id = HANDMADE[h[0]!]?.[0] as PictureId | undefined;
    if (!id) return null;
    return generateShared(h[4]!, h[1]!, { pic: PICTURES[id], arrows: h[2]!, len: h[3]!, teams: h[5] === 1 });
    // تخته فقط با سرِ نما عوض می‌شود
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [h.join(",")]);
  const me = state.watching ? -1 : state.me;
  const players = h[1] ?? state.seats.length;
  const teams = h[5] === 1 && players === 4;
  const tail = tailOf(state.extra, players);
  const brk = useMemo(() => new BreakWatch(), []);
  useEffect(() => { brk.update(state.brk, state.seats, state.me, state.watching); }, [brk, state]);
  /** امتیازها در آغازِ همین دور، تا پنلِ پایانِ دور «+N» ِ همین دور را نشان دهد. */
  const startPoints = useRef<number[] | null>(null);
  if (state.phase === 1 && !state.over) startPoints.current = tail.points;
  const done = (i: number) => (state.extra[4 + i * 3] ?? -1) >= 0;
  const teamDone = (i: number) => done(i % 2) && done((i % 2) + 2);
  /** یار (تیمی)؛ بعد از تمام‌کردنِ خودم فلش‌های او را می‌زنم تا او هم تمام کند. */
  const mate = teams && me >= 0 ? me ^ 2 : -1;
  const helping = mate >= 0 && done(me) && !done(mate);
  const boardRef = useRef<BoardHandle>(null);
  const stageEl = useRef<HTMLDivElement>(null);
  const hud = useRef<HTMLDivElement>(null);
  const dock = useRef<HTMLDivElement>(null);
  const inset = useInsets(hud, dock);
  /** فلش‌هایی که این‌جا رفتند و هنوز در نمای سرور نیامده‌اند: شماره ← لحظه. */
  const pending = useRef(new Map<number, number>());
  const lastTap = useRef(0);
  const [frozenUntil, setFrozenUntil] = useState(0);
  const [freeze, setFreeze] = useState(0);
  const [pulses, setPulses] = useState<number[]>([0, 0, 0, 0]);
  const [, setVer] = useState(0);
  const timerEl = useRef<HTMLSpanElement>(null);
  const graceEl = useRef<HTMLSpanElement>(null);
  /** go به ساعتِ این صفحه (performance.now)؛ با هر نما از نو. */
  const goAt = useRef(0);
  goAt.current = performance.now() - (state.extra[0] ?? 0);
  const firstAt = (state.extra[1] ?? -1) >= 0 ? goAt.current + state.extra[1]! : null;

  const gone = new Set(state.hand);
  const now = performance.now();
  for (const [id, at] of pending.current) {
    if (gone.has(id) || now - at > CONFIRM_MS) pending.current.delete(id);
  }
  const alive = board ? board.arrows.map((a) => !gone.has(a.id) && !pending.current.has(a.id)) : [];
  // دورِ تازه تخته‌ی تازه است: لمس‌های در راه و یخِ دورِ پیش به آن نمی‌رسند
  useEffect(() => { pending.current.clear(); setFrozenUntil(0); }, [board]);

  // رویدادها: پرواز و برخوردِ بقیه، و تأییدِ لمس‌های خودم.
  useEffect(() => client.on((env) => {
    if (env.msg.case !== "boardEvent") return;
    const e = env.msg.value;
    if (e.kind === "go") synth("chime");
    if (e.kind === "out") {
      // جاده‌ی صاحبِ فلش جلو می‌رود، حتی اگر یارش زده باشد
      const owner = board?.arrows[e.a]?.owner ?? e.seat;
      setPulses((p) => p.map((v, k) => (k === owner ? v + 1 : v)));
      if (!pending.current.has(e.a)) boardRef.current?.fly(e.a, e.b);
    }
    if (e.kind === "blocked" && e.seat !== me) boardRef.current?.bump(e.a, e.b);
  }), [me, board]);

  // ساعت و «مانده» بی رندرِ React در هر فریم
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      const t = performance.now();
      // پایانِ دور: ساعت روی لحظه‌ی پایان می‌ایستد
      if (timerEl.current && state.phase === 1 && !state.over) timerEl.current.textContent = clock(Math.max(0, t - goAt.current));
      if (graceEl.current) {
        const mine = me >= 0 && (teams ? teamDone(me) : done(me));
        graceEl.current.textContent = firstAt !== null && !mine && !state.over ? `${clock(GRACE_MS - (t - firstAt))} مانده` : "";
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [state, me, firstAt]);

  const phase = state.over ? 2 : state.phase;
  // پرسشِ صدای بارِ اول وسطِ مسابقه روی تخته می‌افتاد و لمس‌ها را می‌گرفت؛ تا پایان نگهش دار.
  useEffect(() => {
    voiceHold.value = phase === 0 || phase === 1;
    return () => { voiceHold.value = false; };
  }, [phase]);
  /** پشتِ‌همِ فلش‌های خودم (streak.ts). */
  const streak = useRef(newStreak());
  const onTap = (id: number) => {
    if (!board || me < 0 || phase !== 1) return;
    const t = performance.now();
    if (t < frozenUntil || t - lastTap.current < GAP_MS) return;
    lastTap.current = t;
    const a = board.arrows[id]!;
    const block = blockedAt(board, occupancy(board, alive), a);
    client.boardMove(2, id);
    if (block > 0) {
      boardRef.current?.bump(id, block);
      synth("stamp");
      breakStreak(streak.current);
      shake(stageEl.current);
      setFrozenUntil(t + FREEZE_MS);
      setFreeze((f) => f + 1);
      try { navigator.vibrate?.(70); } catch { /* بی لرزش هم کار می‌کند */ }
      return;
    }
    pending.current.set(id, t);
    boardRef.current?.fly(id, ray(board, a).length);
    outSound(streak.current, t);
    setVer((v) => v + 1);
  };

  const totals = useMemo(() => Array.from({ length: players }, (_, s) => board?.arrows.filter((a) => a.owner === s).length ?? 1), [board, players]);
  const lanes: Lane[] = state.seats.slice(0, players).map((s, i) => ({
    name: i === me ? "تو" : botBare(s.name || "حریف", s.bot),
    face: avatarSrc(s.avatar),
    color: PLAYER_COLORS[i]!,
    bot: s.bot,
    cleared: state.score[i] ?? 0,
    total: totals[i] ?? 1,
    done: done(i),
    finishedAt: done(i) ? state.extra[4 + i * 3]! : null,
  }));
  // تیمی: فلش‌های دو یار لای هم‌اند، پس قابِ آغاز نیمه‌ی تیم است
  const focus = useMemo(() => (board && me >= 0 ? teamBox(board, mate >= 0 ? [me, mate] : [me]) : undefined), [board, me, mate]);
  const frozen = freeze > 0 && performance.now() < frozenUntil;
  const left = Math.ceil(-(state.extra[0] ?? 0) / 1000);

  return (
    <div className="table ar-race online">
      <div className="ar-stage" ref={stageEl} onAnimationEnd={(e) => { if (e.target === stageEl.current) stageEl.current.classList.remove("shake"); }}>
        {board && (
          <BoardView ref={boardRef} board={board} alive={alive} onTap={onTap} inset={inset}
            own={me < 0 ? undefined : helping ? mate : me} mates={mate >= 0 ? [helping ? me : mate] : undefined}
            focus={focus} locked={phase !== 1 || me < 0} />
        )}
      </div>

      {frozen && <FreezeRing k={freeze} ms={FREEZE_MS} />}

      <div className="ar-hud" ref={hud}>
        <div className="ar-top solo">
          <div className="ar-clock"><span ref={timerEl}>{clock(0)}</span><small ref={graceEl} /></div>
          {tail.rounds > 1 && <span className="ar-roundchip">دور {fa(Math.min(tail.rounds, tail.round + (phase === 3 ? 0 : 1)))} از {fa(tail.rounds)}</span>}
        </div>
        <Track lanes={lanes} pulses={pulses} teams={teams} me={me} />
        {helping && phase === 1 && (
          <div className="ar-help" key={mate} style={{ "--c": PLAYER_COLORS[mate] } as CSSProperties}>
            <svg viewBox="0 0 24 24" aria-hidden><path d="M7 11V6.5a1.5 1.5 0 0 1 3 0V11m0-1.5v-5a1.5 1.5 0 0 1 3 0v5m0 0v-3a1.5 1.5 0 0 1 3 0V12m0-2.5a1.5 1.5 0 0 1 3 0V15a6 6 0 0 1-6 6h-1.5a6 6 0 0 1-4.6-2.2L4.6 15.6a1.6 1.6 0 0 1 2.4-2.1L7 13.5" /></svg>
            کمک به {lanes[mate]?.name}
          </div>
        )}
      </div>

      <Dock refEl={dock} board={boardRef} />
      {/* چت و ایموجیِ اتاقِ دوستان — همان دکمه‌های میزهای تخته‌ای؛ میکروفون را App پایین-چپ می‌گذارد. */}
      {!state.watching && <div className="ar-chat"><BoardChatButtons onReact={(k) => client.sendCanned(k)} /></div>}

      {phase === 0 && <Intro head={tail.rounds > 1 ? `دور ${fa(tail.round + 1)} از ${fa(tail.rounds)}` : state.watching ? "تماشا" : "مسابقه با دوستان"}
        name={board ? pictureName(HANDMADE[h[0]!]![0]) : "فلش‌ها"} />}
      {phase === 0 && left > 0 && <div className="ar-count" key={left}>{fa(left)}</div>}

      {phase === 3 && <RoundOver state={state} lanes={lanes} me={me} teams={teams} tail={tail} before={startPoints.current} brk={brk} />}
      {phase === 2 && (tail.rounds > 1
        ? <SeriesOver state={state} lanes={lanes} me={me} teams={teams} tail={tail} />
        : <Over state={state} lanes={lanes} me={me} teams={teams} />)}
    </div>
  );
}

/** پایانِ مسابقه — همان کارتِ نتیجه‌ی بقیه‌ی بازی‌ها (Overlay و آگهیِ پایان)، با مدال و جدولِ نتیجه. */
function Over({ state, lanes, me, teams }: { state: BoardTableState; lanes: Lane[]; me: number; teams: boolean }) {
  if (teams) return <TeamOver state={state} lanes={lanes} me={me} />;
  // همان Ranking ِ سرور: تمام‌کرده‌ها به ترتیبِ زمان، بقیه به شمارِ فلش؛ برنده‌ی سرور همیشه اول.
  const fin = (i: number) => lanes[i]!.finishedAt ?? Infinity;
  const order = lanes.map((_, i) => i).sort((a, b) => fin(a) - fin(b) || (state.score[b] ?? 0) - (state.score[a] ?? 0));
  if (state.winner >= 0 && order[0] !== state.winner) {
    order.splice(order.indexOf(state.winner), 1);
    order.unshift(state.winner);
  }
  const won = me >= 0 && order[0] === me;
  const place = me >= 0 ? order.indexOf(me) + 1 : 1;
  const title = me < 0 ? `${lanes[order[0]!]!.name} اول شد` : won ? "اول شدی!" : ["", "", "دوم شدی", "سوم شدی", "چهارم شدی"][place]!;
  return (
    <Overlay label="پایان بازی" card={me < 0 ? "even" : won ? "win" : "lose"} after={<ResultAd won={me < 0 ? null : won} watching={me < 0} />}>
      <div className="vt">{title}</div>
      <Standings lanes={lanes} teams={false} me={me} mistakes={(i) => state.extra[2 + i * 3] ?? 0} />
      <button className="btn" style={{ maxWidth: 260 }} onClick={() => client.exitTable()}>
        {me < 0 ? "پایان تماشا" : "بازگشت به لابی"}
      </button>
    </Overlay>
  );
}

/**
 * پایانِ تیمی: برنده تیمِ برنده‌ی سرور است (Ranking ِ تیمی: اول هر دو نفرِ تیمِ برنده). کمک به یار هم دیده می‌شود.
 */
function TeamOver({ state, lanes, me }: { state: BoardTableState; lanes: Lane[]; me: number }) {
  const win = Math.max(0, state.winner) % 2;
  const won = me >= 0 && me % 2 === win;
  const helped = me >= 0 ? state.extra[2 + lanes.length * 3 + me] ?? 0 : 0;
  const title = me < 0 ? `${lanes[win]!.name} و ${lanes[win + 2]!.name} بردند` : won ? "تیم شما برد!" : "تیم حریف برد";
  return (
    <Overlay label="پایان بازی" card={me < 0 ? "even" : won ? "win" : "lose"} after={<ResultAd won={me < 0 ? null : won} watching={me < 0} />}>
      <div className="vt">{title}</div>
      <Standings lanes={lanes} teams me={me} mistakes={(i) => state.extra[2 + i * 3] ?? 0} />
      {helped > 0 && <div className="muted">{fa(helped)} فلش یارت را هم تو زدی</div>}
      <button className="btn" style={{ maxWidth: 260 }} onClick={() => client.exitTable()}>
        {me < 0 ? "پایان تماشا" : "بازگشت به لابی"}
      </button>
    </Overlay>
  );
}

/**
 * جدولِ امتیازِ میزِ چنددوره: به ترتیبِ امتیاز (برابر: بُردِ بیشتر)، با «+N» ِ همین دور. تیمی: دو ردیفِ تیم، هر کدام با
 * دو چهره — دو یار همیشه امتیازِ برابر دارند.
 */
function SeriesTable({ lanes, me, teams, tail, before }: { lanes: Lane[]; me: number; teams: boolean; tail: Tail; before?: number[] | null }) {
  const gain = (i: number) => (before && before.length === tail.points.length ? (tail.points[i] ?? 0) - (before[i] ?? 0) : 0);
  const rows = teams
    ? [0, 1].map((t) => ({ key: t, seats: [t, t + 2], name: me < 0 ? `تیم ${fa(t + 1)}` : t === me % 2 ? "تیم شما" : "تیم حریف", mine: me >= 0 && t === me % 2 }))
    : lanes.map((l, i) => ({ key: i, seats: [i], name: l.name, mine: i === me }));
  const pts = (r: { seats: number[] }) => tail.points[r.seats[0]!] ?? 0;
  const wins = (r: { seats: number[] }) => tail.wins[r.seats[0]!] ?? 0;
  rows.sort((a, b) => pts(b) - pts(a) || wins(b) - wins(a));
  return (
    <>
    <div className="ar-series-cap">امتیازها</div>
    <ol className="ar-series" aria-label="امتیازها">
      {rows.map((r, k) => {
        const g = gain(r.seats[0]!);
        return (
          <li key={r.key} className={r.mine ? "me" : ""}>
            <span className={`ar-pl p${k + 1}`}>{fa(k + 1)}</span>
            <span className="ar-series-faces">{r.seats.map((i) => <img key={i} src={lanes[i]?.face} alt="" style={{ "--rc": lanes[i]?.color } as CSSProperties} />)}</span>
            <b>{r.name}</b>
            {g > 0 && <small className="ar-gain" key={g}>+{fa(g)}</small>}
            <span className="ar-series-pts">{fa(pts(r))}</span>
          </li>
        );
      })}
    </ol>
    </>
  );
}

/** پنلِ پایانِ دور: نتیجه‌ی همین دور، جدولِ امتیاز، و «دور بعد» با «ادامه» ِ همه. */
function RoundOver({ state, lanes, me, teams, tail, before, brk }: {
  state: BoardTableState; lanes: Lane[]; me: number; teams: boolean; tail: Tail; before: number[] | null; brk: BreakWatch;
}) {
  return (
    <Overlay label="پایان دور" card="even">
      <div className="vt">پایان دور {fa(tail.round)} از {fa(tail.rounds)}</div>
      <Standings lanes={lanes} teams={teams} me={me} mistakes={(i) => state.extra[2 + i * 3] ?? 0} />
      <SeriesTable lanes={lanes} me={me} teams={teams} tail={tail} before={before} />
      <BreakNext watch={brk} label="دور بعد" onNext={() => brk.press()} />
    </Overlay>
  );
}

/** پایانِ میزِ چنددوره: قهرمان با بیشترین امتیاز (رتبه‌ی سرور)، و جدولِ امتیاز. */
function SeriesOver({ state, lanes, me, teams, tail }: { state: BoardTableState; lanes: Lane[]; me: number; teams: boolean; tail: Tail }) {
  const w = Math.max(0, state.winner);
  const won = me >= 0 && (teams ? me % 2 === w % 2 : me === w);
  const title = teams
    ? (me < 0 ? `${lanes[w % 2]!.name} و ${lanes[(w % 2) + 2]!.name} قهرمان شدند` : won ? "تیم شما قهرمان شد!" : "تیم حریف قهرمان شد")
    : (me < 0 ? `${lanes[w]!.name} قهرمان شد` : won ? "قهرمان شدی!" : `${lanes[w]!.name} قهرمان شد`);
  return (
    <Overlay label="پایان بازی" card={me < 0 ? "even" : won ? "win" : "lose"} after={<ResultAd won={me < 0 ? null : won} watching={me < 0} />}>
      <div className="vt">{title}</div>
      <SeriesTable lanes={lanes} me={me} teams={teams} tail={tail} />
      <button className="btn" style={{ maxWidth: 260 }} onClick={() => client.exitTable()}>
        {me < 0 ? "پایان تماشا" : "بازگشت به لابی"}
      </button>
    </Overlay>
  );
}
